import { PROPOSAL_STATES } from './proposal-display'
import { normaliseProposalViewId, proposalView, type ProposalViewId } from './proposals-views'

/** The listing's sorts, as `crmProposals` names them. Every one has a fixed direction. */
export const PROPOSAL_SORTS = ['UPDATED_DESC', 'CREATED_DESC', 'AMOUNT_DESC', 'REFERENCE_ASC'] as const
export type ProposalSort = (typeof PROPOSAL_SORTS)[number]
export const DEFAULT_PROPOSAL_SORT: ProposalSort = 'UPDATED_DESC'

export const PROPOSAL_PAGE_SIZES = [10, 20, 50] as const
export const DEFAULT_PROPOSAL_PAGE_SIZE = 20
/** The server's search cap; a longer term is refused there, so it is cut here. */
export const PROPOSAL_SEARCH_MAX = 100
/** The server refuses an offset past this; a page beyond it is not linkable. */
const OFFSET_MAX = 10_000

export interface ProposalsSearch {
  tab?: ProposalViewId
  state?: string
  /** Free text: deal reference, package, jurisdiction, status or signing state. */
  q?: string
  /** A jurisdiction catalogue key. */
  jurisdiction?: string
  sort?: ProposalSort
  page?: number
  size?: number
}

/**
 * The list's URL state. A value the server would refuse is dropped here, so a stale or hand-edited
 * link shows the list without it instead of an error: an unknown tab, sort or page size, a status the
 * chosen tab does not hold (it would ask for nothing), a page past the server's offset cap. A link
 * from before the free-text search (`dealReference`) becomes that search.
 */
export function validateProposalsSearch(search: Record<string, unknown>): ProposalsSearch {
  const out: ProposalsSearch = {}
  const tab = normaliseProposalViewId(search.tab)
  if (tab) out.tab = tab
  if (typeof search.state === 'string' && (PROPOSAL_STATES as readonly string[]).includes(search.state)) {
    const held = out.tab ? proposalView(out.tab).states : []
    if (held.length === 0 || held.includes(search.state)) out.state = search.state
  }
  const rawQ = typeof search.q === 'string' ? search.q : typeof search.dealReference === 'string' ? search.dealReference : ''
  const q = rawQ.trim().slice(0, PROPOSAL_SEARCH_MAX)
  if (q) out.q = q
  if (typeof search.jurisdiction === 'string' && search.jurisdiction.trim()) out.jurisdiction = search.jurisdiction.trim().slice(0, 100)
  if (typeof search.sort === 'string' && (PROPOSAL_SORTS as readonly string[]).includes(search.sort)) {
    out.sort = search.sort as ProposalSort
  }
  const size = Number(search.size)
  if ((PROPOSAL_PAGE_SIZES as readonly number[]).includes(size)) out.size = size
  const page = Number(search.page)
  const pageSize = out.size ?? DEFAULT_PROPOSAL_PAGE_SIZE
  if (Number.isInteger(page) && page > 1 && (page - 1) * pageSize <= OFFSET_MAX) out.page = page
  return out
}
